// Versão: 2.00
// Data..: 2026-09-24
// Abertura de chamado pelo portal público (sem login), com editor rico (imagens) e
// proteção anti-robô (honeypot + tempo mínimo de preenchimento).

import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PublicShell } from './PublicShell.js';
import { publicApi } from '../../lib/publicApi.js';
import { RichEditor } from '../../components/RichEditor.js';

export function PublicOpenTicketPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', department: 'support' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Anti-robô: honeypot (deve ficar vazio) + carimbo de quando a página abriu.
  const [honeypot, setHoneypot] = useState('');
  const openedAt = useRef<number>(Date.now());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    // Corpo em texto puro para validação (o RichEditor guarda HTML).
    const plain = form.message.replace(/<[^>]*>/g, '').trim();
    if (!plain) { setError('Descreva o problema.'); return; }
    setLoading(true);
    try {
      await publicApi.openTicket({
        ...form,
        company_website: honeypot,
        formOpenedAt: openedAt.current,
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      <div style={{ maxWidth: 620, margin: '40px auto 64px', padding: '0 24px' }}>
        {sent ? (
          <div className="card text-center fade-in" style={{ padding: 'var(--s7)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2>Chamado enviado!</h2>
            <p className="text-muted">
              Recebemos sua solicitação e nossa equipe entrará em contato pelo e-mail informado.
            </p>
            <div className="flex gap-2 justify-center mt-4">
              <Link to="/portal" className="btn">Voltar ao início</Link>
              <button className="btn btn-primary" onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '', department: 'support' }); }}>
                Abrir outro chamado
              </button>
            </div>
          </div>
        ) : (
          <>
            <Link to="/portal" className="text-sm">← Voltar</Link>
            <div className="card mt-4 fade-in">
              <h1>Abrir chamado</h1>
              <p className="text-muted">Preencha os dados abaixo. Você receberá as atualizações por e-mail.</p>
              <form onSubmit={submit} className="mt-4">
                <div className="flex gap-4">
                  <div className="field flex-1">
                    <label className="label">Seu nome</label>
                    <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="field flex-1">
                    <label className="label">E-mail</label>
                    <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="field flex-1">
                    <label className="label">Assunto</label>
                    <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
                  </div>
                  <div className="field" style={{ width: 180 }}>
                    <label className="label">Departamento</label>
                    <select className="select" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                      <option value="support">Suporte</option>
                      <option value="finance">Financeiro</option>
                      <option value="sales">Vendas</option>
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label className="label">Descreva o problema</label>
                  <RichEditor
                    value={form.message}
                    onChange={(html) => setForm({ ...form, message: html })}
                    placeholder="Descreva o problema. Você pode colar ou anexar imagens para ilustrar."
                    minHeight={200}
                    publicUpload
                  />
                </div>
                {/* Honeypot: invisível para humanos; bots tendem a preencher. */}
                <input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  aria-hidden="true"
                />
                {error && <div style={{ color: 'var(--danger)' }} className="mb-4">{error}</div>}
                <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading}>
                  {loading ? 'Enviando...' : 'Enviar chamado'}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
